import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Download, RefreshCw, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as authApi from '@/services/authApi';
import { AttendanceSectionsPicker } from '@/components/admin/AttendanceSectionsPicker';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sites, setSites] = useState([]);
  const [tenantSites, setTenantSites] = useState([]); // multi-tenant sites_registry
  const [sections, setSections] = useState([]);
  const [staffSections, setStaffSections] = useState([]);
  const [modules, setModules] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [staffSearch, setStaffSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    sites: [],
    site_id: 'salem',
    sections: [],
    satt_sections: [],
    modules: [],
    allowed_operations: {},
    phone: '',
    staff_id: ''
  });

  // WMS sections: Section / Processing roles only. Staff Attendance sections: approvers (role or tick) and Reports viewers.
  const isSectionRole = ['Section Incharge', 'Section Supervisor', 'Processing Incharge', 'Processing Supervisor'].includes(formData.role);
  const sattOps = formData.modules.includes('staff_attendance') ? (formData.allowed_operations.staff_attendance || []) : [];
  const roleApproves = /supervisor|incharge/i.test(formData.role || '');
  const sattEnabled = roleApproves || sattOps.includes('satt-approve') || sattOps.includes('satt-reports');
  const sattHrAll = ['admin', 'super_admin', 'site admin', 'hr manager'].includes((formData.role || '').toLowerCase()) || sattOps.includes('satt-hr');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersData, rolesData, sitesData, tenantSitesData, sectionsData, modulesData, staffSectionsData] = await Promise.all([
        authApi.getUsers(),
        authApi.getRoles(),
        authApi.getSites(),
        authApi.getSitesRegistry().catch(() => ({ sites: [] })),
        authApi.getSections(),
        authApi.getModules(),
        authApi.getStaffSections().catch(() => ({ sections: [] }))
      ]);
      setStaffSections(staffSectionsData.sections || []);
      setUsers(usersData.users || []);
      setRoles(rolesData.roles || []);
      setSites(sitesData.sites || []);
      setTenantSites((tenantSitesData.sites || []).filter(s => s.status === 'active'));
      setSections(sectionsData.sections || []);
      setModules(modulesData.modules || []);
      
      // Fetch staff list for picker linking
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('session_token');
        const staffResponse = await fetch(`${API_URL}/api/wms/dispatch/staff-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (staffResponse.ok) {
          const staffData = await staffResponse.json();
          setStaffList(staffData.staff || []);
        }
      } catch (staffError) {
        console.error('Error fetching staff list:', staffError);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: roles[0]?.role_name || '',
      sites: [],
      site_id: 'salem',
      sections: [],
      satt_sections: [],
      modules: [],
      allowed_operations: {},
      phone: '',
      staff_id: ''
    });
    setStaffSearch('');
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      sites: user.sites || [],
      site_id: user.site_id || 'salem',
      sections: user.sections || [],
      satt_sections: user.satt_sections ?? user.sections ?? [],
      modules: user.modules || [],
      allowed_operations: user.allowed_operations || {},
      phone: user.phone || '',
      staff_id: user.staff_id || ''
    });
    setStaffSearch('');
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password || undefined,
        role: formData.role,
        sites: formData.sites || [],
        site_id: formData.site_id || 'salem',
        sections: isSectionRole ? (formData.sections || []) : [],
        satt_sections: sattEnabled ? (formData.satt_sections || []) : [],
        modules: formData.modules || [],
        allowed_operations: formData.allowed_operations || {},
        phone: formData.phone || '',
        staff_id: formData.staff_id || null,
        created_by_google: false
      };
      
      console.log('Saving user:', editingUser ? 'UPDATE' : 'CREATE', userData);
      
      if (editingUser) {
        const result = await authApi.updateUser(editingUser.user_id, userData);
        console.log('Update result:', result);
        toast.success('User updated successfully');
      } else {
        const result = await authApi.createUser(userData);
        console.log('Create result:', result);
        toast.success('User created successfully');
      }
      setShowUserModal(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save user';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete user ${user.name}?`)) return;
    try {
      await authApi.deleteUser(user.user_id);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user, newStatus) => {
    try {
      await authApi.toggleUserStatus(user.user_id, newStatus);
      toast.success(`User status changed to '${newStatus}'`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleApproveUser = async (user) => {
    try {
      await authApi.toggleUserStatus(user.user_id, 'active');
      toast.success(`User ${user.name} approved successfully`);
      fetchData();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Sites', 'Modules', 'Phone', 'Status'];
    const rows = users.map(u => [
      u.name,
      u.email,
      u.role,
      u.sites?.join(';') || 'None',
      u.modules?.join(';') || 'None',
      u.phone || 'N/A',
      u.status || 'unknown'
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_${Date.now()}.csv`;
    link.click();
    toast.success('Users exported to CSV');
  };

  const toggleSiteSelection = (siteName) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.includes(siteName)
        ? prev.sites.filter(s => s !== siteName)
        : [...prev.sites, siteName]
    }));
  };

  const toggleModuleSelection = (moduleId) => {
    setFormData(prev => {
      const isRemoving = prev.modules.includes(moduleId);
      const newModules = isRemoving
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId];
      const newOps = { ...prev.allowed_operations };
      if (isRemoving) {
        delete newOps[moduleId];
      }
      return { ...prev, modules: newModules, allowed_operations: newOps };
    });
  };

  const toggleOperationSelection = (moduleId, opId) => {
    setFormData(prev => {
      const currentOps = prev.allowed_operations[moduleId] || [];
      const newOps = currentOps.includes(opId)
        ? currentOps.filter(o => o !== opId)
        : [...currentOps, opId];
      return {
        ...prev,
        allowed_operations: { ...prev.allowed_operations, [moduleId]: newOps }
      };
    });
  };

  const toggleAllOperations = (moduleId, allOps) => {
    setFormData(prev => {
      const currentOps = prev.allowed_operations[moduleId] || [];
      const allSelected = allOps.every(op => currentOps.includes(op.op_id));
      return {
        ...prev,
        allowed_operations: {
          ...prev.allowed_operations,
          [moduleId]: allSelected ? [] : allOps.map(op => op.op_id)
        }
      };
    });
  };

  const toggleSectionSelection = (sectionName) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionName)
        ? prev.sections.filter(s => s !== sectionName)
        : [...prev.sections, sectionName]
    }));
  };

  const filteredUsers = (() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const haystack = [
        u.name, u.email, u.role, u.staff_id, u.phone, u.site_id, u.status,
        Array.isArray(u.sites) ? u.sites.join(' ') : '',
        Array.isArray(u.sections) ? u.sections.join(' ') : '',
        Array.isArray(u.satt_sections) ? u.satt_sections.join(' ') : '',
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  })();

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold font-display">User Management</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExportCSV} variant="secondary" className="h-10" data-testid="export-csv-button">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
            <Button onClick={fetchData} variant="secondary" className="h-10"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">Manage Users</h2>
            <Button onClick={handleCreateUser} className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />Add User
            </Button>
          </div>
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F9E]" />
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name, email, role, staff ID, phone, site or section..."
                className="pl-9 pr-9"
                data-testid="user-search-input"
              />
              {userSearch && (
                <button
                  onClick={() => setUserSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8F9E] hover:text-[#1A1F2E]"
                  data-testid="user-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {userSearch && (
              <p className="text-xs text-[#5A5F6E] mt-2" data-testid="user-search-count">
                Showing {filteredUsers.length} of {users.length} users
              </p>
            )}
          </div>
          {isLoading ? (
            <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" /></div>
          ) : (
            <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                      <TableHead className="font-semibold text-[#1A1F2E]">User</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Role</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Site</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Sites</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Sections</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Staff ID</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Contact</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Status</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-[#8A8F9E]" data-testid="user-search-empty">
                          {userSearch ? `No users match "${userSearch}"` : 'No users found'}
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.map((user) => {
                      const isPending = user.status === 'pending';
                      const isActive = user.status === 'active' || user.status === true;
                      const isInactive = user.status === 'inactive' || user.status === false;
                      const isSectionUser = ['Section Incharge', 'Section Supervisor', 'Processing Incharge', 'Processing Supervisor'].includes(user.role);
                      
                      return (
                      <TableRow key={user.user_id} className="hover:bg-[#FFFBF5]">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-[#1A1F2E]">{user.name}</p>
                              <p className="text-sm text-[#5A5F6E]">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{user.role}</span></TableCell>
                        <TableCell className="text-sm">
                          <span className="inline-block px-2 py-0.5 rounded font-mono text-[11px] bg-purple-50 text-purple-700 border border-purple-200" data-testid={`user-site-${user.user_id}`}>
                            {user.site_id || 'salem'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-[#5A5F6E]">{user.sites?.length > 0 ? user.sites.join(', ') : 'None'}</TableCell>
                        <TableCell className="text-sm text-[#5A5F6E]">
                          {isSectionUser ? (user.sections?.length > 0 ? user.sections.join(', ') : 'None') : '-'}
                          {user.satt_sections?.length > 0 && (
                            <div className="text-[11px] text-amber-700 mt-0.5" data-testid={`user-satt-sections-${user.user_id}`}>Attendance: {user.satt_sections.join(', ')}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-[#5A5F6E]">
                          {user.staff_id ? (
                            <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                              {user.staff_id}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-[#5A5F6E]">{user.phone || 'N/A'}</TableCell>
                        <TableCell>
                          {isPending && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending Approval
                            </span>
                          )}
                          {isActive && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                          {isInactive && (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            {isPending && (
                              <Button 
                                onClick={() => handleApproveUser(user)} 
                                size="sm" 
                                className="h-8 bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-user-${user.user_id}`}
                              >
                                Approve
                              </Button>
                            )}
                            {isActive && (
                              <Button 
                                onClick={() => handleToggleStatus(user, 'inactive')} 
                                variant="outline"
                                size="sm" 
                                className="h-8 border-red-500 text-red-500 hover:bg-red-50"
                                data-testid={`deactivate-user-${user.user_id}`}
                              >
                                Deactivate
                              </Button>
                            )}
                            {isInactive && (
                              <Button 
                                onClick={() => handleToggleStatus(user, 'active')} 
                                variant="outline"
                                size="sm" 
                                className="h-8 border-green-500 text-green-500 hover:bg-green-50"
                                data-testid={`activate-user-${user.user_id}`}
                              >
                                Activate
                              </Button>
                            )}
                            <Button onClick={() => handleEditUser(user)} variant="ghost" size="sm" className="h-8" data-testid={`edit-user-${user.user_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button onClick={() => handleDeleteUser(user)} variant="ghost" size="sm" className="h-8 hover:bg-red-50 hover:text-red-600" data-testid={`delete-user-${user.user_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter name" className="mt-1" data-testid="user-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter email" className="mt-1" data-testid="user-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Password {!editingUser && '*'}</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'} className="mt-1" data-testid="user-password-input" /></div>
              <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone number" className="mt-1" data-testid="user-phone-input" /></div>
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, sections: [] })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.role_id} value={role.role_name} data-testid={`user-role-option-${role.role_name}`}>{role.role_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.role === 'HR Manager' && (
                <p className="text-[11px] text-slate-600 mt-1" data-testid="user-role-hr-hint">
                  HR Manager manages all of Staff Attendance on this site — every section, approvals, Daily / Monthly / CSV, work locations, shifts &amp; settings. No other admin areas.
                </p>
              )}
            </div>

            {/* Multi-tenant Site assignment — controls which site this user can log in to.
                Super Admin (role='Admin') can log in to ANY site regardless of selection. */}
            <div>
              <Label className="flex items-center gap-2">
                Site *
                <span className="text-xs text-[#5A5F6E]">— tenant the user is assigned to (login is rejected on other sites)</span>
              </Label>
              <Select
                value={formData.site_id}
                onValueChange={(value) => setFormData({ ...formData, site_id: value })}
              >
                <SelectTrigger className="mt-1" data-testid="user-site-select">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {tenantSites.length === 0 ? (
                    <SelectItem value="salem">Salem (default)</SelectItem>
                  ) : tenantSites.map((s) => (
                    <SelectItem key={s.site_id} value={s.site_id} data-testid={`user-site-option-${s.site_id}`}>
                      {s.site_name} {s.site_code ? `(${s.site_code})` : ''}{s.is_default ? ' · default' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(formData.role || '').toLowerCase() === 'admin' && (
                <p className="text-[11px] text-purple-700 mt-1">
                  Super Admins (Admin role) bypass the site-match check at login.
                </p>
              )}
            </div>
            
            {/* WMS Sections - Section / Processing Incharge & Supervisor (consignments, transfers, alerts, dispatch rights) */}
            <div className={!isSectionRole ? 'opacity-50' : ''} data-testid="user-sections-block">
              <Label className="flex items-center gap-2">
                WMS Sections (Multi-select)
                {!isSectionRole && <span className="text-xs text-[#5A5F6E]">- Only for Section / Processing Incharge or Supervisor</span>}
              </Label>
              <div className={`mt-2 border border-[#E5DFD0] rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto ${!isSectionRole ? 'pointer-events-none' : ''}`}>
                {sections.length > 0 ? sections.map((section) => (
                  <label key={section.section_id || section.section_name} className="flex items-center gap-2 cursor-pointer hover:bg-[#F5F1E8] p-2 rounded">
                    <input 
                      type="checkbox" 
                      checked={formData.sections.includes(section.section_name)} 
                      onChange={() => toggleSectionSelection(section.section_name)} 
                      className="w-4 h-4"
                      disabled={!isSectionRole}
                      data-testid={`user-section-checkbox-${section.section_id || section.section_name}`}
                    />
                    <span className="text-sm text-[#1A1F2E]">{section.section_name}</span>
                  </label>
                )) : (
                  <p className="text-sm text-[#5A5F6E]">No sections available. Add sections first.</p>
                )}
              </div>
            </div>

            <AttendanceSectionsPicker
              options={staffSections}
              value={formData.satt_sections}
              onChange={(v) => setFormData((prev) => ({ ...prev, satt_sections: v }))}
              enabled={sattEnabled}
              hrAll={sattHrAll}
              reportsOnly={!roleApproves && !sattOps.includes('satt-approve')}
            />
            
            <div>
              <Label>Modules & Operations (Multi-select)</Label>
              <p className="text-xs text-[#5A5F6E] mb-2">Select modules, then choose which operations the user can access</p>
              <div className="mt-1 border border-[#E5DFD0] rounded-lg p-3 space-y-1 max-h-72 overflow-y-auto" data-testid="modules-operations-list">
                {modules.map((module) => {
                  const isChecked = formData.modules.includes(module.module_id);
                  const hasOps = module.operations && module.operations.length > 0;
                  const isExpanded = expandedModules[module.module_id];
                  const selectedOps = formData.allowed_operations[module.module_id] || [];
                  const allOpsSelected = hasOps && module.operations.every(op => selectedOps.includes(op.op_id));
                  
                  return (
                    <div key={module.module_id} className="rounded-lg border border-transparent hover:border-[#E5DFD0]">
                      <div className="flex items-center gap-2 p-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleModuleSelection(module.module_id)}
                          className="w-4 h-4 flex-shrink-0"
                          data-testid={`module-checkbox-${module.module_id}`}
                        />
                        <span
                          className={`text-sm flex-1 cursor-pointer ${isChecked ? 'text-[#1A1F2E] font-medium' : 'text-[#5A5F6E]'}`}
                          onClick={() => toggleModuleSelection(module.module_id)}
                        >
                          {module.module_name}
                        </span>
                        {isChecked && hasOps && (
                          <button
                            type="button"
                            onClick={() => setExpandedModules(prev => ({ ...prev, [module.module_id]: !prev[module.module_id] }))}
                            className="flex items-center gap-1 text-xs text-[#2D5F4F] hover:underline px-2 py-1 rounded"
                            data-testid={`expand-ops-${module.module_id}`}
                          >
                            {selectedOps.length}/{module.operations.length} ops
                            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                      {isChecked && hasOps && isExpanded && (
                        <div className="ml-6 pb-2 pr-2 space-y-1" data-testid={`ops-list-${module.module_id}`}>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-[#F5F1E8] p-1.5 rounded border-b border-[#E5DFD0] mb-1">
                            <input
                              type="checkbox"
                              checked={allOpsSelected}
                              onChange={() => toggleAllOperations(module.module_id, module.operations)}
                              className="w-3.5 h-3.5"
                            />
                            <span className="text-xs font-semibold text-[#2D5F4F]">Select All</span>
                          </label>
                          {module.operations.map((op) => (
                            <label key={op.op_id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F5F1E8] p-1.5 rounded">
                              <input
                                type="checkbox"
                                checked={selectedOps.includes(op.op_id)}
                                onChange={() => toggleOperationSelection(module.module_id, op.op_id)}
                                className="w-3.5 h-3.5"
                                data-testid={`op-checkbox-${module.module_id}-${op.op_id}`}
                              />
                              <span className="text-xs text-[#1A1F2E]">{op.op_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Staff ID for Picker Allocation */}
            <div>
              <Label className="flex items-center gap-2">
                Link to Staff (for Picker Allocation)
                <span className="text-xs text-[#5A5F6E]">- Optional, used for WMS pick slip assignment</span>
              </Label>
              <div className="mt-2 space-y-2">
                <Input
                  placeholder="Search staff by name or ID..."
                  value={staffSearch}
                  onChange={(e) => setStaffSearch(e.target.value)}
                  className="mb-2"
                />
                <Select 
                  value={formData.staff_id || ''} 
                  onValueChange={(value) => setFormData({ ...formData, staff_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="w-full" data-testid="user-staff-select">
                    <SelectValue placeholder="Select staff to link" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">-- No staff linked --</SelectItem>
                    {staffList
                      .filter(s => 
                        !staffSearch || 
                        s.staff_name?.toLowerCase().includes(staffSearch.toLowerCase()) ||
                        s.staff_id?.toLowerCase().includes(staffSearch.toLowerCase())
                      )
                      .slice(0, 50)
                      .map((staff) => (
                        <SelectItem key={staff.staff_id} value={staff.staff_id}>
                          {staff.staff_name} ({staff.staff_id})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {formData.staff_id && (
                  <p className="text-xs text-green-600">✓ Linked to: {formData.staff_id}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" onClick={() => setShowUserModal(false)} variant="secondary" className="flex-1">Cancel</Button>
            <Button type="button" onClick={handleSaveUser} className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D] text-white" data-testid="save-user-button">{editingUser ? 'Update User' : 'Create User'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;